/**
 * Main markdown rendering component with reference citation support
 * Renders AI responses with full markdown support including code highlighting,
 * math expressions, tables, and numbered reference citations
 */

import type { Message } from "@apis:message/message";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import { createMarkdownComponents } from "./markdown/components";
import { MARKDOWN_STYLES } from "./markdown/constants";
import { preprocessMarkdownText } from "./markdown/utils";

interface MarkdownWithReferencesProps {
  text: string;
  references: Message["GET_ONE"]["Response"]["docs"];
  className?: string;
}

const logError = (
  functionName: string,
  error: unknown,
  context?: Record<string, unknown>,
) => {
  console.error(`[MarkdownWithReferences.${functionName}]`, error, context);

  // Send to error tracking (Sentry)
  Sentry.captureException(error, {
    tags: { function: functionName, component: "MarkdownWithReferences" },
    extra: context,
  });
};

export const MarkdownWithReferences: React.FC<MarkdownWithReferencesProps> = ({
  text,
  references,
  className,
}) => {
  const processedText = useMemo(() => {
    try {
      return preprocessMarkdownText(text, references);
    } catch (error) {
      logError("preprocess", error, {
        textLength: text.length,
        referencesCount: references.length,
      });
      return text;
    }
  }, [text, references]);

  const components = useMemo(
    () => createMarkdownComponents(references),
    [references],
  );

  return (
    <Box className={className} sx={MARKDOWN_STYLES.container}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {processedText}
      </ReactMarkdown>
    </Box>
  );
};
